import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  alpha,
  CircularProgress
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import {
  ChevronDown,
  PanelLeftClose,
  PanelRightOpen,
  Info,
  Mail,
  Globe,
  Newspaper,
  MessageSquare,
  Search
} from 'lucide-react';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSelector } from 'react-redux';
import { selectLevel1Data, selectLevel2Data, selectFeatureMetricsLoading, selectFeatureMetricsError } from 'store/dataReducers/featureMetricsSlice';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { useWidgetSdk } from 'context/WidgetSdkContext';
import { useAvailableFuelTypes } from 'hooks/useProjectFuelTypes';
import { useBulkManageData } from 'utils/template-list';
import {
  transformToHierarchicalData,
  getFuelTypeLabel,
  normalizeFuelType,
  type ChannelWithTemplates
} from 'utils/templates-from-feature-metrics';

type Props = {
  selectedTemplateKey?: string;
  onSelect: (templateKey: string) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  disabled?: boolean; // Disable switching templates while preview is rendering
  fuelFilter?: string | null; // When set (e.g. ELECTRIC, GAS), sidebar shows only templates of that fuel type
};

const channelIcons: Record<string, React.ElementType> = {
  Email: Mail,
  HER: Newspaper,
  Web: Globe,
  SMS: MessageSquare,
};

const channelIconFor = (name: string) => {
  const Icon = channelIcons[name];
  return Icon ? <Icon size={16} /> : null;
};

export const TemplatesSidebar: React.FC<Props> = ({ selectedTemplateKey, onSelect, isOpen = true, onToggleOpen, disabled = false, fuelFilter = null }) => {
  const { rc } = useGlobalConfig();
  const level1Data = useSelector(selectLevel1Data);
  const level2Data = useSelector(selectLevel2Data);
  const loading = useSelector(selectFeatureMetricsLoading);
  const error = useSelector(selectFeatureMetricsError);
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const { isReady: isWidgetSdkReady } = useWidgetSdk();
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  // Same source as DataScenariosSidebar: API-driven list of project fuel types (e.g. ELECTRIC, GAS, DUAL)
  const { availableFuelTypes: projectFuelTypes } = useAvailableFuelTypes(rc, selectedPilotId, true);
  const fuelType = React.useMemo(() => projectFuelTypes[0] || 'ELECTRIC', [projectFuelTypes]);

  const { data: bulkManageData, loading: bulkManageLoading, error: bulkManageError } = useBulkManageData(rc, selectedPilotId);

  // Same template list as Data Scenarios sidebar (shared transform + same projectFuelTypes source)
  const data = React.useMemo((): ChannelWithTemplates[] | null => {
    if (!level1Data || !Array.isArray(level1Data)) return null;
    return transformToHierarchicalData(
      level1Data,
      level2Data,
      fuelType,
      projectFuelTypes,
      false,
      bulkManageData
    );
  }, [level1Data, level2Data, fuelType, projectFuelTypes, bulkManageData]);

  const filteredData = React.useMemo((): ChannelWithTemplates[] | null => {
    if (!data) return null;
    let result = data;
    if (fuelFilter) {
      const normalizedFilter = normalizeFuelType(fuelFilter);
      result = result
        .map(channel => ({
          ...channel,
          templates: (channel.templates || []).filter(
            t => normalizeFuelType(t.fuelType) === normalizedFilter
          )
        }))
        .filter(ch => ch.templates.length > 0);
    }
    if (!searchQuery.trim()) return result;
    const query = searchQuery.toLowerCase();
    return result
      .map(channel => ({
        ...channel,
        templates: (channel.templates || []).filter(
          t =>
            t.name?.toLowerCase().includes(query) ||
            t.templateKey?.toLowerCase().includes(query)
        )
      }))
      .filter(ch => ch.templates.length > 0);
  }, [data, searchQuery, fuelFilter]);

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 0, width: '100%', minWidth: isOpen ? 300 : 48, overflow: 'hidden' }}>
      <CardHeader
        sx={{
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 56
        }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isOpen && (
              <>
                <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                  CX Templates
                </Typography>
                <Tooltip title="These are the templates currently in scope for the project.">
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    <Info size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </div>
        }
        action={
          onToggleOpen ? (
            <Tooltip title={isOpen ? 'Collapse Templates bar' : 'Expand Templates bar'} placement={isOpen ? 'top' : 'right'}>
              <IconButton size="small" onClick={onToggleOpen} sx={{ ml: 'auto' }}>
                {isOpen ? <PanelLeftClose size={16} /> : <PanelRightOpen size={16} />}
              </IconButton>
            </Tooltip>
          ) : null
        }
      />
      <CardContent sx={{ pt: 1.5, px: 2, pb: 2, flex: 1, display: isOpen ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ py: 2, px: 1 }}>{error}</Typography>
        )}

        {!loading && !error && (
          <>
            <TextField
              placeholder="Search templates"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 1.5 }}
            />

            {(filteredData || []).map(ch => {
              const isWebChannel = ch.name === 'Web';
              const isDisabled = isWebChannel && !isWidgetSdkReady;
              
              return (
                <Accordion 
                  key={ch.name}
                  disableGutters 
                  elevation={0} 
                  defaultExpanded={ch.name === 'Email'}
                  disabled={isDisabled}
                  sx={{ 
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 },
                    opacity: isDisabled ? 0.5 : 1
                  }}
                >
                  <Tooltip 
                    title={isDisabled ? 'Widget SDK is initializing. Web templates will be available once the SDK is ready.' : ''}
                    placement="right"
                  >
                    <AccordionSummary 
                      expandIcon={<ChevronDown size={16} />}
                      sx={{
                        minHeight: 40,
                        '&.Mui-expanded': { minHeight: 40 },
                        px: 1,
                        cursor: isDisabled ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          color: isDisabled ? 'text.disabled' : 'text.secondary', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}
                      >
                        {channelIconFor(ch.name)}
                        {ch.name}
                      </Typography>
                    </AccordionSummary>
                  </Tooltip>
                  <AccordionDetails sx={{ pt: 0.5, pb: 0.5, px: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(ch.templates || []).map(t => {
                      const active = t.templateKey === selectedTemplateKey;
                      const fuelLabel = getFuelTypeLabel(t.fuelType);
                      const clickDisabled = disabled || (isWebChannel && !isWidgetSdkReady);

                      return (
                        <Tooltip key={t.templateKey} title={t.templateKey} placement="right">
                          <Box
                            onClick={() => {
                              if (clickDisabled) return;
                              onSelect(t.templateKey);
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              gap: 1,
                              width: '100%',
                              px: 1.5,
                              py: 1.5,
                              cursor: clickDisabled ? 'not-allowed' : 'pointer',
                              opacity: clickDisabled ? 0.6 : 1,
                              borderRadius: 1,
                              textAlign: 'left',
                              border: active ? theme => `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                              backgroundColor: active 
                                ? theme => theme.palette.mode === 'dark' 
                                  ? alpha(theme.palette.primary.main, 0.16) 
                                  : alpha(theme.palette.primary.main, 0.08)
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: (theme) =>
                                  clickDisabled
                                    ? 'transparent'
                                    : theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.common.white, 0.08)
                                      : alpha(theme.palette.common.black, 0.04)
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={fuelLabel}
                              sx={{
                                fontSize: '0.75rem',
                                height: 20,
                                px: 1.5,
                                py: 0.5,
                                borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                                color: 'primary.main',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                flexShrink: 0
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                fontSize: '0.875rem',
                                textAlign: 'left'
                              }}
                            >
                              {t.name}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
              );
            })}
          </>
        )}
        </div>
      </CardContent>
    </Card>
  );
}
